import {
  createContext,
  useContext,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAsyncRetry } from 'react-use'
import type { CrmLeadCardInFunnelStatusHistoryItem } from '@/api/types/crm/crmFunnels.types'
import { LeadStatus, Status } from '@/api/types/crm/crm.types'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useLeadRefetch } from '../../LeadRefetchContext'
import { fetchLeadCardInFunnelDataDeduped } from '../../leadModalRefetchMerge'

interface LeadStatusContextType {
  statuses: LeadStatus[]
  loading: boolean
  refreshStatuses: () => void
}

const LeadStatusContext = createContext<LeadStatusContextType | undefined>(
  undefined,
)

interface LeadStatusProviderProps {
  leadId: number
  funnelId: number | null
  children: ReactNode
  onNeedUpdateLead?: () => void
}

export const LeadStatusProvider: FC<LeadStatusProviderProps> = ({
  leadId,
  funnelId,
  children,
  onNeedUpdateLead,
}) => {
  const addAlert = useAddAlert()
  const {
    getCardInFunnelSnapshot,
    cardFunnelSnapshotEpoch,
  } = useLeadRefetch()
  const normalizedFunnelId =
    funnelId !== null && Number.isFinite(Number(funnelId)) && Number(funnelId) > 0
      ? Number(funnelId)
      : null

  const mapStatusHistoryToLeadStatus = (
    item: CrmLeadCardInFunnelStatusHistoryItem,
  ): LeadStatus => {
    const fallbackStatus: Status = {
      id: item.status_id,
      name: item.status_name,
      color: item.status_color,
      unique_name: '',
      is_auto_new: false,
      is_auto_processed: false,
      is_auto_after_purchase: false,
      is_bad_lead: false,
      is_protected: false,
      is_client_auto_new: false,
      sorting: 0,
    }

    return {
      id: item.id,
      created_at: item.created_at,
      status: fallbackStatus,
    }
  }

  const [useSnapshotMode, setUseSnapshotMode] = useState(true)

  useEffect(() => {
    setUseSnapshotMode(true)
  }, [cardFunnelSnapshotEpoch, normalizedFunnelId, leadId])

  const snapshotStatuses = useMemo(() => {
    if (!normalizedFunnelId) return undefined as LeadStatus[] | undefined
    const snap = getCardInFunnelSnapshot(normalizedFunnelId)
    if (!snap) return undefined
    const history = snap.status_history
    if (!Array.isArray(history)) return undefined
    return history.map(mapStatusHistoryToLeadStatus)
  }, [
    normalizedFunnelId,
    getCardInFunnelSnapshot,
    cardFunnelSnapshotEpoch,
  ])

  const state = useAsyncRetry(async () => {
    if (!normalizedFunnelId) {
      return [] as LeadStatus[]
    }

    const snap = getCardInFunnelSnapshot(normalizedFunnelId)
    if (snap && Array.isArray(snap.status_history)) {
      return snap.status_history.map(mapStatusHistoryToLeadStatus)
    }

    const payload = await fetchLeadCardInFunnelDataDeduped(
      leadId,
      normalizedFunnelId,
    )
    const statusHistory = payload?.status_history ?? []
    return statusHistory.map(mapStatusHistoryToLeadStatus)
  }, [leadId, normalizedFunnelId, cardFunnelSnapshotEpoch, getCardInFunnelSnapshot])

  const statuses = useSnapshotMode
    ? (snapshotStatuses ?? state.value ?? [])
    : (state.value ?? [])

  const loading =
    normalizedFunnelId === null
      ? false
      : useSnapshotMode
        ? snapshotStatuses === undefined && state.loading
        : state.loading

  useEffect(() => {
    if (state.error) {
      const backendError = (state.error as any)?.response?.data?.error
      const errorText = Array.isArray(backendError)
        ? backendError.join(' ')
        : String(backendError ?? '')
      const isLeadFunnelNotFound =
        errorText.toLowerCase().includes('lead funnel not found') ||
        errorText.toLowerCase().includes('воронка не найдена')

      if (isLeadFunnelNotFound) return

      addAlert({
        title: 'Ошибка',
        text: 'Не удалось загрузить статусы заявки',
        type: 'error',
      })
    }
  }, [state.error, addAlert])

  const handlerRefetchStatuses = () => {
    setUseSnapshotMode(false)
    state.retry()
    onNeedUpdateLead && onNeedUpdateLead()
  }

  return (
    <LeadStatusContext.Provider
      value={{
        statuses,
        loading,
        refreshStatuses: handlerRefetchStatuses,
      }}
    >
      {children}
    </LeadStatusContext.Provider>
  )
}

export const useLeadStatuses = () => {
  const context = useContext(LeadStatusContext)
  if (!context) {
    throw new Error('useLeadStatuses must be used within a LeadStatusProvider')
  }
  return context
}
